import React from 'react';
import { Card, Group, Table, Text, ThemeIcon } from '@mantine/core';
import type { IFilaTotalesPlanes, IResultadoFamiliaServicio } from '../../../modelos/CotizadorServicios/entidades/IResultadoCotizacionServicios';
import { formatoMoneda } from '../interfaces';

/** Columna propia de la familia (capacidad, paradas, altura...). */
export interface IColumnaFamilia {
    encabezado: string;
    valor: (linea: IResultadoFamiliaServicio['Lineas'][number]) => string;
}

interface TablaResumenFamiliaVistaProps {
    titulo: string;
    icono: React.ReactNode;
    resultado: IResultadoFamiliaServicio;
    columnas: IColumnaFamilia[];
    porcentajeIva: number;
}

const celdasPlanes = (fila: IFilaTotalesPlanes, negrita = false) => (
    <>
        <Table.Td ta="right" fw={negrita ? 700 : undefined}>{formatoMoneda(fila.PlanConekta.Unitario)}</Table.Td>
        <Table.Td ta="right" fw={negrita ? 700 : undefined}>{formatoMoneda(fila.PlanConekta.Total)}</Table.Td>
        <Table.Td ta="right" fw={negrita ? 700 : undefined}>{formatoMoneda(fila.PlanConektaPlus.Unitario)}</Table.Td>
        <Table.Td ta="right" fw={negrita ? 700 : undefined}>{formatoMoneda(fila.PlanConektaPlus.Total)}</Table.Td>
    </>
);

/** Tabla de la cotización de una familia, con los dos planes y los totales con IVA (hoja "Cotizador"). */
export const TablaResumenFamiliaVista: React.FC<TablaResumenFamiliaVistaProps> = ({ titulo, icono, resultado, columnas, porcentajeIva }) => {
    // Cantidad + Referencia + columnas propias: ancho de la etiqueta de los totales.
    const columnasDescripcion = 2 + columnas.length;

    return (
        <Card withBorder radius="md" p="md">
            <Group gap="xs" mb="sm">
                <ThemeIcon variant="light" color="primary" radius="xl" size="md">{icono}</ThemeIcon>
                <Text fw={600} size="md">{titulo}</Text>
            </Group>
            <Table.ScrollContainer minWidth={800}>
                <Table withTableBorder withColumnBorders fz="xs" verticalSpacing={6}>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th rowSpan={2}>Cant.</Table.Th>
                            <Table.Th rowSpan={2}>Referencia</Table.Th>
                            {columnas.map(c => <Table.Th key={c.encabezado} rowSpan={2}>{c.encabezado}</Table.Th>)}
                            <Table.Th colSpan={2} ta="center">Plan Conekta</Table.Th>
                            <Table.Th colSpan={2} ta="center">Plan Conekta Plus</Table.Th>
                        </Table.Tr>
                        <Table.Tr>
                            <Table.Th ta="right">Unidad</Table.Th>
                            <Table.Th ta="right">Total</Table.Th>
                            <Table.Th ta="right">Unidad</Table.Th>
                            <Table.Th ta="right">Total</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {resultado.Lineas.map(linea => (
                            <Table.Tr key={linea.EquipoId}>
                                <Table.Td>{linea.Cantidad}</Table.Td>
                                <Table.Td>{linea.Referencia}</Table.Td>
                                {columnas.map(c => <Table.Td key={c.encabezado}>{c.valor(linea)}</Table.Td>)}
                                {celdasPlanes(linea)}
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                    <Table.Tfoot>
                        <Table.Tr>
                            <Table.Th colSpan={columnasDescripcion} ta="right">Total sin IVA</Table.Th>
                            {celdasPlanes(resultado.Totales.SinIva)}
                        </Table.Tr>
                        <Table.Tr>
                            <Table.Th colSpan={columnasDescripcion} ta="right">IVA {porcentajeIva}%</Table.Th>
                            {celdasPlanes(resultado.Totales.Iva)}
                        </Table.Tr>
                        <Table.Tr bg="var(--mantine-color-gray-0)">
                            <Table.Th colSpan={columnasDescripcion} ta="right">Total con IVA</Table.Th>
                            {celdasPlanes(resultado.Totales.ConIva, true)}
                        </Table.Tr>
                    </Table.Tfoot>
                </Table>
            </Table.ScrollContainer>
        </Card>
    );
};
